// Quality tier logic for Nexus Forge crafting.
//
// Tiers: poor < common < good < fine < masterwork
// Each crafted item carries a quality suffix: "copper_wire:common"
// Raw gathered resources have no quality suffix.
//
// Roll formula (from balance.crafting.quality):
//   targetQuality = clamp(base + skillXp * multiplier, base, cap)
//   variance      = clamp(baseVariance - skillXp * decay, minVariance, baseVariance)
//   roll          = triangular(target - variance, target, target + variance)
//   score         = clamp(roll + uniform_jitter(±jitterMagnitude), 0, 100)

import { balance } from "./balance.ts";

export type QualityTier = "poor" | "common" | "good" | "fine" | "masterwork";

const QUALITY_TIERS: readonly QualityTier[] = [
  "poor",
  "common",
  "good",
  "fine",
  "masterwork",
];

export function isQualityTier(s: string): s is QualityTier {
  return (QUALITY_TIERS as readonly string[]).includes(s);
}

// -----------------------------------------------------------------------
// Bucket lookup
// -----------------------------------------------------------------------

const { buckets } = balance.crafting;

// Maps a continuous [0, 100] quality score to its named tier.
export function scoreToTier(score: number): QualityTier {
  if (score <= buckets.poor.max) return "poor";
  if (score <= buckets.common.max) return "common";
  if (score <= buckets.good.max) return "good";
  if (score <= buckets.fine.max) return "fine";
  return "masterwork";
}

// -----------------------------------------------------------------------
// Roll logic
// -----------------------------------------------------------------------

const { quality: qb } = balance.crafting;

// Triangular distribution sample given min=a, mode=c, max=b, u=uniform[0,1].
// Standard inverse-CDF formula. Exported for unit testing.
export function sampleTriangular(a: number, c: number, b: number, u: number): number {
  const fc = (c - a) / (b - a);
  if (u < fc) {
    return a + Math.sqrt(u * (b - a) * (c - a));
  }
  return b - Math.sqrt((1 - u) * (b - a) * (b - c));
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// Rolls a quality score [0, 100] for a completed craft given the crafter's
// current XP in the relevant primary skill.
// NOT pure — calls Math.random() twice (triangular sample + jitter).
export function rollQuality(skillXp: number): number {
  const target = clamp(
    qb.baseTargetQuality + skillXp * qb.skillQualityMultiplier,
    qb.baseTargetQuality,
    qb.targetQualityCap,
  );
  const variance = clamp(
    qb.baseVariance - skillXp * qb.varianceDecay,
    qb.minVariance,
    qb.baseVariance,
  );

  const lo = target - variance;
  const hi = target + variance;
  const raw = sampleTriangular(lo, target, hi, Math.random());
  const jitter = (Math.random() * 2 - 1) * qb.jitterMagnitude; // contracts:allow-literal — math identity [-1,1], not a tuning value
  return clamp(raw + jitter, 0, 100); // contracts:allow-literal — 100 is the definitional quality scale ceiling
}

// -----------------------------------------------------------------------
// Quality display
// -----------------------------------------------------------------------

export const QUALITY_COLORS: Record<QualityTier, number> = {
  poor: 0x808080,
  common: 0xffffff,
  good: 0x4caf50,
  fine: 0x2196f3,
  masterwork: 0x9c27b0,
};

export const QUALITY_LABELS: Record<QualityTier, string> = {
  poor: "Poor",
  common: "Common",
  good: "Good",
  fine: "Fine",
  masterwork: "Masterwork",
};

// -----------------------------------------------------------------------
// ItemId composition and parsing
// -----------------------------------------------------------------------

// Produces the inventory item ID for a crafted item: e.g. "copper_wire:common".
// Raw resources are stored without a suffix — call this only for crafted outputs.
export function qualityItemId(baseId: string, quality: QualityTier): string {
  return `${baseId}:${quality}`;
}

// Splits a full inventory item ID into its base and optional quality tier.
// Returns { baseId: "copper", quality: null } for raw resources.
// Returns { baseId: "copper_wire", quality: "common" } for crafted items.
export function parseItemId(fullId: string): { baseId: string; quality: QualityTier | null } {
  const colonAt = fullId.indexOf(":");
  if (colonAt === -1) {
    return { baseId: fullId, quality: null };
  }
  const baseId = fullId.slice(0, colonAt);
  const suffix = fullId.slice(colonAt + 1);
  return { baseId, quality: isQualityTier(suffix) ? suffix : null };
}
